import React, { useState, useCallback } from "react";
import Modal from "@mui/material/Modal";
import { CloudUpload } from "@mui/icons-material";
import { Download } from "lucide-react";
import { ExcelImportProps, ImportSummary, ImportProgress } from "../../types/UserImport";
import { processExcelImport, generateExcelTemplate } from "../../utils/ExcelImportUtils";
import { getUserIdFromLocalStorage } from "../../API_Endpoints/APIEndpoints";
import { BASE_URL } from "../../App_Settings/app_settings";
import FileUploadSection from "./FileUploadSection";
import ImportPreview from "./ImportPreview";
import ImportProgressComponent from "./ImportProgress";
import "./UserImport.css";

interface UserImportModalProps extends ExcelImportProps {}

const UserImportModal: React.FC<UserImportModalProps> = ({
  show,
  onClose,
  onImportComplete,
  availableRoles,
  availableBusinessUnits,
  availableOrganizations,
  currentUserOrganizationId,
  isSuperAdmin,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importSummary, setImportSummary] = useState<ImportSummary | null>(null);
  const [finalSummary, setFinalSummary] = useState<ImportSummary | null>(null);
  const [progress, setProgress] = useState<ImportProgress | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentStep, setCurrentStep] = useState<"upload" | "preview" | "importing" | "complete">(
    "upload"
  );

  const handleClose = useCallback(() => {
    if (!isProcessing) {
      setSelectedFile(null);
      setImportSummary(null);
      setFinalSummary(null);
      setProgress(null);
      setCurrentStep("upload");
      onClose();
    }
  }, [isProcessing, onClose]);

  const handleFileSelect = useCallback(
    async (file: File) => {
      setSelectedFile(file);
      setIsProcessing(true);
      setProgress({
        stage: "parsing",
        current: 0,
        total: 0,
        message: "Parsing Excel file...",
        percentage: 10,
      });

      try {
        const userData = getUserIdFromLocalStorage();
        const summary = await processExcelImport(
          file,
          availableRoles,
          availableBusinessUnits,
          availableOrganizations,
          currentUserOrganizationId || userData.organizationId,
          userData.userName || "System",
          isSuperAdmin
        );

        setImportSummary(summary);
        setCurrentStep("preview");
        setProgress({
          stage: "complete",
          current: summary.totalRows,
          total: summary.totalRows,
          message: "File processed successfully",
          percentage: 100,
        });
      } catch (error) {
        console.error("Import processing error:", error);
        alert(`Error processing file: ${error instanceof Error ? error.message : "Unknown error"}`);
        setCurrentStep("upload");
      } finally {
        setIsProcessing(false);
      }
    },
    [
      availableRoles,
      availableBusinessUnits,
      availableOrganizations,
      currentUserOrganizationId,
      isSuperAdmin,
    ]
  );

  const handleDownloadTemplate = useCallback(async () => {
    try {
      const templateBlob = await generateExcelTemplate(
        availableRoles,
        availableBusinessUnits,
        availableOrganizations,
        isSuperAdmin
      );

      const url = URL.createObjectURL(templateBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `user-import-template-${new Date().toISOString().split("T")[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating template:", error);
      alert("Error generating template file");
    }
  }, [availableRoles, availableBusinessUnits, availableOrganizations, isSuperAdmin]);

  const handleImport = useCallback(async () => {
    if (!importSummary) return;

    const successfulUsers = importSummary.results.filter((r) => r.success && r.processed);
    if (successfulUsers.length === 0) {
      alert("No valid users to import");
      return;
    }

    setIsProcessing(true);
    setCurrentStep("importing");
    setProgress({
      stage: "saving",
      current: 0,
      total: successfulUsers.length,
      message: "Creating users...",
      percentage: 0,
    });

    try {
      const userData = getUserIdFromLocalStorage();
      let successCount = 0;
      let errorCount = 0;
      // Track per-email import errors immutably
      const importErrorMap: Record<string, string> = {};

      for (let i = 0; i < successfulUsers.length; i++) {
        const userResult = successfulUsers[i];

        setProgress({
          stage: "saving",
          current: i + 1,
          total: successfulUsers.length,
          message: `Creating user ${i + 1} of ${successfulUsers.length}: ${userResult.user.email}`,
          percentage: Math.round(((i + 1) / successfulUsers.length) * 100),
        });

        try {
          const response = await fetch(`${BASE_URL}/Users`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${userData.token}`,
            },
            body: JSON.stringify(userResult.processed),
          });

          if (response.ok) {
            successCount++;
          } else {
            errorCount++;
            let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
            try {
              const contentType = response.headers.get("content-type") || "";
              if (contentType.includes("application/json")) {
                const errorBody = await response.json();
                errorMessage =
                  errorBody.message || errorBody.title || JSON.stringify(errorBody) || errorMessage;
              } else {
                const text = (await response.text()).trim();
                if (text) errorMessage = text;
              }
            } catch {
              // keep the HTTP status message
            }
            console.error(`Failed to create user ${userResult.user.email}: ${errorMessage}`);
            importErrorMap[userResult.user.email] = errorMessage;
          }
        } catch (error) {
          errorCount++;
          const msg = error instanceof Error ? error.message : "Network error";
          console.error(`Error creating user ${userResult.user.email}:`, error);
          importErrorMap[userResult.user.email] = msg;
        }

        // Small delay to prevent overwhelming the server
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      // Build updated results array immutably so React sees the change
      const updatedResults = importSummary.results.map((r) => {
        const err = importErrorMap[r.user.email];
        return err ? { ...r, importError: err } : r;
      });

      const completedSummary: ImportSummary = {
        ...importSummary,
        results: updatedResults,
        successCount,
        errorCount,
      };

      setProgress({
        stage: "complete",
        current: successfulUsers.length,
        total: successfulUsers.length,
        message:
          errorCount > 0
            ? `Import complete: ${successCount} users created successfully, ${errorCount} failed`
            : `Import complete: ${successCount} users created successfully`,
        percentage: 100,
      });

      setCurrentStep("complete");
      setFinalSummary(completedSummary);

      // Auto-close only when ALL users imported with zero errors
      if (errorCount === 0) {
        setTimeout(() => {
          onImportComplete(completedSummary);
          handleClose();
        }, 2000);
      } else {
        // Keep modal open — user must review errors and close manually
        onImportComplete(completedSummary);
      }
    } catch (error) {
      console.error("Import error:", error);
      setCurrentStep("preview");
    } finally {
      setIsProcessing(false);
    }
  }, [importSummary, onImportComplete, handleClose]);

  const handleRetry = useCallback(() => {
    setCurrentStep("upload");
    setSelectedFile(null);
    setImportSummary(null);
    setProgress(null);
  }, []);

  const canImport = importSummary && importSummary.successCount > 0;

  return (
    <Modal open={show} onClose={handleClose}>
      <div className="user-import-modal">
        <div className="user-import-header">
          <h2 className="user-import-title">
            <CloudUpload />
            Import Users from Excel
          </h2>
          <p className="user-import-subtitle">Upload an Excel file to bulk import user accounts</p>
        </div>

        <div className="user-import-content">
          {/* Template Download Section */}
          {!selectedFile && currentStep === "upload" && (
            <div className="template-section">
              <p className="template-text">
                Download the Excel template to ensure your data is in the correct format for import.
              </p>
              <button className="template-button" onClick={handleDownloadTemplate}>
                <Download size={16} />
                Download Template
              </button>
            </div>
          )}

          {/* File Upload Section */}
          {currentStep === "upload" && (
            <FileUploadSection
              onFileSelect={handleFileSelect}
              isProcessing={isProcessing}
              selectedFile={selectedFile}
            />
          )}

          {/* Progress Section — only while actively importing, not during preview */}
          {progress && currentStep === "importing" && (
            <ImportProgressComponent progress={progress} />
          )}

          {/* Preview Section */}
          {currentStep === "preview" && importSummary && (
            <ImportPreview summary={importSummary} onRetry={handleRetry} />
          )}

          {/* Complete Section */}
          {currentStep === "complete" && progress && (
            <div className="progress-container">
              <ImportProgressComponent progress={progress} />
              {finalSummary && finalSummary.errorCount > 0 && (
                <div
                  style={{
                    marginTop: "12px",
                    padding: "12px 16px",
                    backgroundColor: "#fff3e0",
                    borderRadius: "6px",
                    border: "1px solid #ffcc02",
                    fontSize: "13px",
                    color: "#424242",
                  }}
                >
                  <strong>{finalSummary.errorCount} user(s) failed to import:</strong>
                  <table
                    style={{
                      width: "100%",
                      marginTop: "10px",
                      borderCollapse: "collapse",
                      fontSize: "12px",
                    }}
                  >
                    <thead>
                      <tr style={{ borderBottom: "1px solid #ffcc02" }}>
                        <th
                          style={{
                            textAlign: "left",
                            padding: "4px 8px",
                            color: "#5d4037",
                            width: "60px",
                          }}
                        >
                          Row
                        </th>
                        <th style={{ textAlign: "left", padding: "4px 8px", color: "#5d4037" }}>
                          Email
                        </th>
                        <th style={{ textAlign: "left", padding: "4px 8px", color: "#5d4037" }}>
                          Reason
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {finalSummary.results
                        .map((r, idx) => ({ r, rowNum: idx + 2 }))
                        .filter(({ r }) => r.importError)
                        .map(({ r, rowNum }, i) => (
                          <tr key={i} style={{ borderBottom: "1px solid #ffe0b2" }}>
                            <td style={{ padding: "5px 8px", color: "#888", fontWeight: 600 }}>
                              Row {rowNum}
                            </td>
                            <td style={{ padding: "5px 8px", color: "#424242" }}>
                              {r.user?.email}
                            </td>
                            <td style={{ padding: "5px 8px", color: "#d32f2f" }}>
                              {r.importError}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Actions */}
        <div className="modal-actions">
          <button
            className="cancel-btn-filled"
            style={{ width: "100px" }}
            onClick={handleClose}
            disabled={isProcessing}
            type="button"
          >
            {currentStep === "complete" ? "Close" : "Cancel"}
          </button>

          {currentStep === "preview" && canImport && (
            <button
              className="action-button primary"
              onClick={handleImport}
              disabled={isProcessing}
            >
              {isProcessing ? (
                <>
                  <div className="loading-spinner" />
                  Importing...
                </>
              ) : (
                `Import ${importSummary.successCount} Users`
              )}
            </button>
          )}

          {currentStep === "preview" && !canImport && (
            <button className="review-btn-outline" onClick={handleRetry}>
              Try Another File
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default UserImportModal;
